import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../auth/session';
import { Bouton } from '../ui';
import { dateComplete } from './dates';
import { useReglagesUrgence } from './reglagesUrgence';
import { sonAutorise, sonner, type Sonnerie } from './sonnerie';
import { LIBELLE_MOTIF, alerteAImposer, type Alerte } from './urgence';

/**
 * L'alerte urgente reçue, imposée à l'écran jusqu'à l'accusé.
 *
 * C'est le seul endroit du produit qui interrompt. Le reste — pastilles, listes,
 * notifications — informe et laisse choisir le moment ; ici le moment est le sujet.
 *
 * Trois précautions, qui ne sont pas des détails :
 *
 * - **Le cadre pulse lentement**, une fois toutes les deux secondes. Un clignotement rapide
 *   déclenche des crises chez les personnes photosensibles (WCAG 2.3.1 pose la limite à
 *   trois par seconde) ; sur un écran qu'on ouvre parce qu'un enfant a un problème, c'est
 *   un risque qu'on ne prend pas. Sous `prefers-reduced-motion`, le cadre ne bouge plus
 *   du tout : il reste rouge et plein, ce qui suffit.
 * - **Accuser réception n'est pas répondre**, et le bouton le dit. L'accusé arrête les
 *   rappels ; il n'engage à rien et ne vaut pas accord — c'est ce qui permet de le poser
 *   sans y réfléchir à deux fois, ce qui est exactement l'usage recherché.
 * - **Rien ne se ferme sans accusé** : ni la touche Échap, ni le clic à côté. C'est la
 *   seule fenêtre du produit dans ce cas, et c'est justifié par le fait qu'un accusé
 *   arrête une sonnerie qui, sinon, revient toutes les minutes.
 */
export function AlerteRecue({
  caseFileId,
  monUserId,
}: {
  caseFileId: string;
  monUserId: string | null;
}) {
  const { client } = useSession();
  const queryClient = useQueryClient();
  const dialogue = useRef<HTMLDialogElement>(null);
  const sonnerie = useRef<Sonnerie | null>(null);
  const reglages = useReglagesUrgence();
  const [sonBloque, setSonBloque] = useState(false);

  const alertes = useQuery({
    queryKey: ['alertes', caseFileId],
    queryFn: () => client.request<Alerte[]>(`/case-files/${caseFileId}/alerts`),
    // Filet de sécurité derrière le temps réel : si la connexion permanente est tombée,
    // une alerte urgente ne doit pas attendre un rechargement de page.
    refetchInterval: 30_000,
  });

  const alerte = alerteAImposer(alertes.data, monUserId, reglages.urgence);

  const accuser = useMutation({
    mutationFn: (alertId: string) =>
      client.request(`/alerts/${alertId}/acknowledgement`, { method: 'POST' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['alertes', caseFileId] }),
  });

  useEffect(() => {
    const element = dialogue.current;
    if (!element) return;
    if (alerte && !element.open) element.showModal?.();
    if (!alerte && element.open) element.close();
  }, [alerte]);

  /**
   * La sonnerie suit l'alerte, et s'arrête avec elle.
   *
   * Le nettoyage est la partie qui compte : sans lui, un accusé de réception laisserait le
   * son tourner dans un onglet, et il n'y aurait plus aucun moyen de l'arrêter — ce qui
   * transformerait le dispositif en la nuisance qu'il prétend encadrer.
   */
  const doitSonner = alerte !== null && alerte.sound && reglages.son;
  useEffect(() => {
    if (!doitSonner) return;
    if (!sonAutorise()) {
      // Un navigateur refuse de produire du son tant que la page n'a rien reçu comme
      // geste. On le dit plutôt que de laisser croire que ça sonne.
      setSonBloque(true);
      return;
    }
    setSonBloque(false);
    sonnerie.current = sonner();
    return () => {
      sonnerie.current?.arreter();
      sonnerie.current = null;
    };
  }, [doitSonner, alerte?.id]);

  if (!alerte) return null;

  return (
    <>
      {/* Le cadre est purement décoratif pour les techniques d'assistance : la fenêtre
          porte déjà le message, et un lecteur d'écran n'a que faire d'une bordure. */}
      <div className="cadre-urgence" aria-hidden="true" />

      <dialog
        className="alerte-recue"
        ref={dialogue}
        aria-labelledby="alerte-recue-titre"
        onCancel={(evenement) => evenement.preventDefault()}
      >
        <div className="alerte-recue-contenu">
          <p className="alerte-recue-motif">Alerte urgente · {LIBELLE_MOTIF[alerte.reason]}</p>
          <h2 id="alerte-recue-titre">{alerte.body}</h2>
          <p className="discret">Déposée le {dateComplete(new Date(alerte.createdAt))}</p>

          {sonBloque ? (
            <Bouton
              variante="secondaire"
              onClick={() => {
                sonnerie.current = sonner();
                setSonBloque(false);
              }}
            >
              Activer le son
            </Bouton>
          ) : null}

          <Bouton
            onClick={() => {
              sonnerie.current?.arreter();
              sonnerie.current = null;
              accuser.mutate(alerte.id);
            }}
            disabled={accuser.isPending}
          >
            {accuser.isPending ? 'Envoi…' : 'J’ai vu'}
          </Bouton>
          <p className="discret" style={{ marginBottom: 0 }}>
            Accuser réception arrête les rappels. Cela ne vaut ni réponse, ni accord.
          </p>

          {/* La sortie de secours est **dans** la fenêtre qui dérange, pas enfouie dans un
              écran de réglages qu'il faudrait aller chercher. Quelqu'un qu'on réveille pour
              la troisième fois n'ira pas explorer un menu ; il doit pouvoir couper là où
              ça sonne. */}
          <ReglagesUrgenceEnLigne />
        </div>
      </dialog>
    </>
  );
}


/**
 * Les deux défenses, offertes au moment où elles servent.
 *
 * Elles sont rangées dans un `<details>` replié : elles doivent être atteignables sans
 * être suggérées. Déplié d'office, il proposerait de désactiver l'alerte à quelqu'un qui
 * vient de la recevoir pour une bonne raison — et la première alerte légitime serait la
 * dernière à passer.
 */
function ReglagesUrgenceEnLigne() {
  const reglages = useReglagesUrgence();

  return (
    <details className="reglages-urgence">
      <summary>Trop d’alertes ?</summary>
      <p className="discret">
        Ces réglages sont les vôtres. L’autre parent n’en est pas informé, et ce qu’il
        dépose reste enregistré et lisible dans le dossier.
      </p>
      <label>
        <input
          type="checkbox"
          checked={!reglages.son}
          disabled={reglages.enregistrer.isPending}
          onChange={() => reglages.enregistrer.mutate({ urgentSound: !reglages.son })}
        />
        <span>Couper le son des alertes urgentes</span>
      </label>
      <label>
        <input
          type="checkbox"
          checked={!reglages.urgence}
          disabled={reglages.enregistrer.isPending}
          onChange={() => reglages.enregistrer.mutate({ urgentTakeover: !reglages.urgence })}
        />
        <span>
          Désactiver l’urgence
          <span className="discret">
            Plus de sonnerie, plus de rappel toutes les minutes, plus de fenêtre imposée.
            Les alertes arrivent comme les autres notifications.
          </span>
        </span>
      </label>
    </details>
  );
}
